import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Input,
  Label,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Table,
} from 'reactstrap';
import classnames from 'classnames';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import { reverseApi, getAllTransactionApi, assignApi } from 'api/transaction';
import { getAllAllMembersApi } from 'api/member'; // Use the same API as Member component
import { useMutation } from 'react-query';
import { Link, useLocation } from 'react-router-dom';
import notify from 'utils/notify';
import { useRole } from 'hooks/useRole'; // For role-based scoping
import { useAuth } from 'hooks/useAuth'; // For authentication status

const TransactionEdit = () => {
  const location = useLocation();
  const { transactionNumber } = location.state || {};
  const [activeTab, setActiveTab] = useState(1);
  const [accountNumber, setAccountNumber] = useState('');
  const [transaction, setTransaction] = useState(null);
  const [member, setMember] = useState(null);

  // Authentication and role hooks
  const { isAuthenticated } = useAuth();
  const {
    isFacilitator,
    isSiteCord,
    isCountyCoordinator,
    isClusterLeader,
    isFinance,
    isDirector,
    isAdmin,
    isSuperAdmin,
    isData,
    addresses,
  } = useRole();

  // Mirrors the backend 'transaction:reassign' grant — field roles never see this page.
  const canManageTransactions = isSuperAdmin || isAdmin || isFinance || isDirector;

  // Extract address fields from the user's profile
  const { wardId, constituencyId, countyId, regionId } = isAuthenticated
    ? addresses[0] || {}
    : {};

  // Mutation for reversing transaction
  const { mutate: reverseTransaction, isLoading: isReversing } = useMutation(
    (id) => reverseApi(id),
    {
      onSuccess: () => {
        notify.success('Success', 'Transaction reversed successfully');
      },
      onError: (error) => {
        notify.error('Error', error.message || 'Failed to reverse transaction');
      },
    },
  );

  // Mutation for searching transaction
  const { mutate: searchTransaction, isLoading: isSearching } = useMutation(
    (payload) => getAllTransactionApi(payload),
    {
      onSuccess: (data) => {
        const transactionData = data?.list?.[0];
        if (transactionData) {
          setTransaction(transactionData);
          setAccountNumber(transactionData.account || '');
        } else {
          notify.error('Error', 'No transaction found with this number');
        }
      },
      onError: () => {
        notify.error('Error', 'Failed to fetch transaction details');
      },
    },
  );

  // Mutation for searching member by ID with role-based scoping
  const { mutate: searchMember, isLoading: isSearchingMember } = useMutation(
    (payload) => getAllAllMembersApi(payload),
    {
      onSuccess: (data) => {
        console.log('Member Search Response:', data); // Debug
        const memberData = data?.list?.[0]; // Extract first member from list
        if (memberData && memberData.memberId && memberData.identificationNumber) {
          setMember(memberData);
          notify.success('Member Found', `Member ${memberData.fullName || 'N/A'} found`);
        } else {
          setMember(null);
          notify.error('Error', 'No member found with this account number');
        }
      },
      onError: (error) => {
        console.error('Search Error:', error); // Debug
        setMember(null);
        notify.error('Error', error.message || 'Failed to search member');
      },
    },
  );

  // Mutation for assigning transaction
  const { mutate: assignTransaction, isLoading: isAssigning } = useMutation(
    (payload) => assignApi(payload),
    {
      onSuccess: () => {
        notify.success('Success', `Transaction reassigned to account: ${accountNumber}`);
      },
      onError: (error) => {
        notify.error('Error', error.message || 'Failed to reassign transaction');
      },
    },
  );

  // Fetch transaction details on mount
  useEffect(() => {
    if (transactionNumber) {
      const payload = {
        pageSize: 10,
        pageNumber: 0,
        sortBy: 'id',
        ascendingSort: false,
        searchColumns: [{ name: 'transactionNumber', value: transactionNumber }],
      };
      searchTransaction(payload);
    } else {
      notify.warning('Warning', 'No transaction number provided');
    }
  }, [transactionNumber, searchTransaction]);

  // Handle account number search with role-based scoping
  const handleSearchAccount = () => {
    if (!accountNumber) {
      notify.warning('Warning', 'Please enter an account number');
      return;
    }
    if (!/^\d+$/.test(accountNumber)) {
      notify.warning('Invalid Input', 'Account number must be numeric');
      return;
    }

    // Construct searchColumns with ID number and role-based address scoping
    const searchColumns = [
      { name: 'idNumber', value: accountNumber }, // Search by ID number
      ...(isFacilitator && wardId ? [{ name: 'wardId', value: wardId }] : []),
      ...(isCountyCoordinator && countyId ? [{ name: 'countyId', value: countyId }] : []),
      ...(isClusterLeader && regionId ? [{ name: 'regionId', value: regionId }] : []),
      ...(isSiteCord && constituencyId ? [{ name: 'constituencyId', value: constituencyId }] : []),
    ].filter(Boolean); // Remove any falsy values (e.g., undefined address fields)

    const payload = {
      pageSize: 20,
      pageNumber: 0,
      sortBy: 'id',
      ascendingSort: false,
      searchColumns,
    };

    console.log('Search Payload:', payload); // Debug
    searchMember(payload);
  };

  // Handle transaction assignment
  const handleReassignConfirm = () => {
    if (!transaction?.transactionNumber) {
      notify.warning('Warning', 'No transaction selected');
      return;
    }
    if (!member) {
      notify.warning('Warning', 'Please search and verify the account number first');
      return;
    }

    notify
      .confirm({
        title: 'Reassign transaction',
        text: `Reassign this transaction to account ${accountNumber}?`,
        confirmText: 'Reassign',
      })
      .then((ok) => {
        if (ok) {
          const assignPayload = {
            transactionNumber: transaction.transactionNumber,
            memberId: member.memberId,
          };
          assignTransaction(assignPayload);
        }
      });
  };

  const handleReverseConfirm = () => {
    if (!transaction?.id) {
      notify.warning('Warning', 'No transaction selected for reversal');
      return;
    }

    notify
      .confirm({
        title: 'Reverse transaction',
        text: 'Reversing this transaction will cancel it and refund any associated payments. This action cannot be undone.',
        confirmText: 'Reverse',
        danger: true,
      })
      .then((ok) => {
        if (ok) reverseTransaction(transaction.id);
      });
  };

  document.title = `Edit Transaction #${transaction?.transactionNumber || transactionNumber || 'N/A'} | SUN Welfare Administration System`;

  console.log('Current Member State:', member); // Debug

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Transactions" breadcrumbItem="Edit Transaction" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <h4 className="card-title mb-4">
                    Edit Transaction #{transaction?.transactionNumber || transactionNumber || 'N/A'}
                  </h4>
                  <Link to="/payments">
                    <Button type="button" color="primary" className="btn mb-4">
                      <i className="bx bx-arrow-back"></i> Back
                    </Button>
                  </Link>

                  <CardTitle className="h4">Transaction Details</CardTitle>
                  {isSearching ? (
                    <p>Loading transaction details...</p>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table mb-0 table-striped">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Transaction Number</th>
                            <th>Amount</th>
                            <th>Account Number</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{transaction?.id || 'N/A'}</td>
                            <td>{transaction?.transactionNumber || 'N/A'}</td>
                            <td>{transaction?.amount || 'N/A'}</td>
                            <td>{transaction?.account || 'N/A'}</td>
                            <td>{transaction?.status || 'N/A'}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  )}

                  {canManageTransactions && (
                  <div className="wizard clearfix mt-4">
                    <div className="steps clearfix">
                      <ul>
                        <NavItem className={classnames({ current: activeTab === 1 })}>
                          <NavLink
                            className={classnames({ current: activeTab === 1 })}
                            onClick={() => setActiveTab(1)}
                          >
                            <span className="number"></span> Reverse
                          </NavLink>
                        </NavItem>
                        <NavItem className={classnames({ current: activeTab === 2 })}>
                          <NavLink
                            className={classnames({ current: activeTab === 2 })}
                            onClick={() => setActiveTab(2)}
                          >
                            <span className="number"></span> Reassign
                          </NavLink>
                        </NavItem>
                      </ul>
                    </div>

                    <div className="content clearfix">
                      <TabContent activeTab={activeTab} className="body">
                        <TabPane tabId={1}>
                          {transaction?.status === 'Reversed' ? (
                            <p className="text-danger">
                              This transaction is already reversed.
                            </p>
                          ) : (
                            <>
                              <p>
                                Reversing this transaction will cancel it and refund any associated payments. This action cannot be undone.
                              </p>
                              <Button
                                color="danger"
                                onClick={handleReverseConfirm}
                                disabled={isReversing || !transaction?.id}
                              >
                                {isReversing ? 'Reversing...' : 'Reverse Transaction'}
                              </Button>
                            </>
                          )}
                        </TabPane>

                        <TabPane tabId={2}>
                          <p>
                            Reassign this transaction to a different account number. First, search to verify the account exists, then assign.
                          </p>
                          <div className="mb-3">
                            <Label>Account Number (ID Number)</Label>
                            <Input
                              type="text"
                              value={accountNumber}
                              onChange={(e) => {
                                setAccountNumber(e.target.value);
                                setMember(null);
                              }}
                              placeholder="Enter correct account number"
                            />
                          </div>
                          <Button
                            color="primary"
                            onClick={handleSearchAccount}
                            disabled={isSearchingMember || !accountNumber}
                            className="me-2"
                          >
                            {isSearchingMember ? 'Searching...' : 'Search Account'}
                          </Button>
                          <Button
                            color="warning"
                            onClick={handleReassignConfirm}
                            disabled={isAssigning || !member || !transaction?.transactionNumber}
                          >
                            {isAssigning ? 'Assigning...' : 'Reassign Transaction'}
                          </Button>

                          {member && member.memberId && (
                            <div className="mt-3">
                              <h5>Verified Member Details</h5>
                              <Table className="table mb-0 table-striped">
                                <thead>
                                  <tr>
                                    <th>Name</th>
                                    <th>ID Number</th>
                                    <th>Member ID</th>
                                    <th>Status</th>
                                    <th>Phone</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <td>{member.fullName || 'N/A'}</td>
                                    <td>{member.identificationNumber || 'N/A'}</td>
                                    <td>{member.memberId || 'N/A'}</td>
                                    <td>{member.status || 'N/A'}</td>
                                    <td>{member.phoneNumber1 || 'N/A'}</td>
                                  </tr>
                                </tbody>
                              </Table>
                            </div>
                          )}
                        </TabPane>
                      </TabContent>
                    </div>
                  </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TransactionEdit;
